import { useState, useEffect, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calculator, FileText, Printer, Download, AlertTriangle, ArrowLeft, Plus, Trash2 } from "lucide-react";
import ProjectSupportDialog from "@/components/ProjectSupportDialog";
import { calculateSundryTotal } from "@/components/SundryItemsManager";
import HelpTooltip, { HELP_CONTENT } from "@/components/HelpTooltip";
import QuoteTargetSelector from "@/components/QuoteTargetSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
};

const AtlasAccessControl = ({ onBack, backLabel = "Back to Access Control" }) => {
  // Project info
  const [projectName, setProjectName] = useState("");
  const [quoteReference, setQuoteReference] = useState("");
  const [priceModifier, setPriceModifier] = useState("");
  const [userRole, setUserRole] = useState("");
  const [quoteTarget, setQuoteTarget] = useState(""); // "distributor" or "installer"
  const [installerOnlyCapture, setInstallerOnlyCapture] = useState(false);
  
  // NEW ORDER: Step 1: What credential type?
  const [credentialType, setCredentialType] = useState("");
  
  // Step 2: Do you require read in/read out?
  const [requiresReadInOut, setRequiresReadInOut] = useState("");
  
  // Step 3: How many doors?
  const [totalDoors, setTotalDoors] = useState("");
  
  // Step 4: How many doors are read in/read out? (conditional)
  const [readInOutDoors, setReadInOutDoors] = useState("");
  
  // Results
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Project Support
  const [showProjectSupport, setShowProjectSupport] = useState(false);
  const [supportDeclined, setSupportDeclined] = useState(false);
  
  // Sundry Items
  const [sundryItems, setSundryItems] = useState([]);
  const [showSundryDialog, setShowSundryDialog] = useState(false);
  const [editingSundryIndex, setEditingSundryIndex] = useState(null);
  const [sundryForm, setSundryForm] = useState({
    sku: '',
    description: '',
    quantity: 1,
    unit_price: 0
  });
  
  // Quote box ref for download
  const quoteBoxRef = useRef(null);

  // Sundry Items Functions
  const handleAddSundryItem = () => {
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
    setShowSundryDialog(true);
  };

  const handleEditSundryItem = (index) => {
    setSundryForm(sundryItems[index]);
    setEditingSundryIndex(index);
    setShowSundryDialog(true);
  };

  const handleSaveSundryItem = () => {
    if (!sundryForm.description || sundryForm.quantity <= 0) {
      setError('Please enter a description and valid quantity');
      return;
    }
    
    const newItem = {
      ...sundryForm,
      quantity: parseFloat(sundryForm.quantity) || 1,
      unit_price: parseFloat(sundryForm.unit_price) || 0,
      line_total: (parseFloat(sundryForm.quantity) || 1) * (parseFloat(sundryForm.unit_price) || 0)
    };
    
    if (editingSundryIndex !== null) {
      const updated = [...sundryItems];
      updated[editingSundryIndex] = newItem;
      setSundryItems(updated);
    } else {
      setSundryItems([...sundryItems, newItem]);
    }
    
    setShowSundryDialog(false);
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
  };

  const handleDeleteSundryItem = (index) => {
    if (window.confirm('Remove this sundry item?')) {
      setSundryItems(sundryItems.filter((_, i) => i !== index));
    }
  };

  const handleDownloadQuoteImage = async (installerOnly = false) => {
    if (!quoteBoxRef.current) return;
    try {
      if (installerOnly) {
        setInstallerOnlyCapture(true);
        await new Promise(r => setTimeout(r, 250));
      }
      const canvas = await html2canvas(quoteBoxRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });
      const suffix = installerOnly ? 'Installer' : (isDistributorUser ? 'Distributor' : '');
      const link = document.createElement('a');
      link.download = `${projectName || 'Atlas'}${suffix ? '_' + suffix : ''}_Quote.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading quote:', err);
      setError('Failed to download quote image');
    } finally {
      if (installerOnly) setInstallerOnlyCapture(false);
    }
  };

  // Validation states
  const [canProceedToReadInOut, setCanProceedToReadInOut] = useState(false);
  const [canProceedToDoors, setCanProceedToDoors] = useState(false);
  const [canProceedToReadInOutCount, setCanProceedToReadInOutCount] = useState(false);
  const [canCalculate, setCanCalculate] = useState(false);
  
  // Warning for 85+ doors
  const [showBioCVWarning, setShowBioCVWarning] = useState(false);

  // Fetch user role on mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
        setUserRole(response.data.role);
      } catch (err) {
        console.error('Error fetching user role:', err);
      }
    };
    fetchUserRole();
  }, []);

  const handleNewQuote = () => {
    setSupportDeclined(false); // Reset for new quote
    console.log('New Quote button clicked (Atlas)');
    
    // Check if there's any data entered
    const hasData = projectName || credentialType || totalDoors;
    
    // Check if current work is saved (Atlas doesn't have config ID tracking yet)
    const hasResults = result !== null;
    
    if (hasData) {
      let message = "Start a new quote?";
      if (hasResults) {
        message = "Start a new quote?\n\nYour current quote appears to be calculated. Make sure it's saved if needed.";
      } else {
        message = "Start a new quote?\n\nAny unsaved changes will be lost.";
      }
      
      const confirmed = window.confirm(message);
      if (!confirmed) {
        console.log('User cancelled new quote');
        return;
      }
    }
    
    console.log('Clearing form to start new quote...');
    
    // Reset all state to initial values
    setProjectName("");
    setQuoteReference("");
    setPriceModifier("");
    setCredentialType("");
    setRequiresReadInOut("");
    setTotalDoors("");
    setReadInOutDoors("");
    setResult(null);
    setError("");
    setShowBioCVWarning(false); // Reset warning
    setSuccess("✓ New quote started! Enter project details below.");
    setTimeout(() => setSuccess(""), 4000);
    
    console.log('Form cleared - ready for new quote');
  };

  // Update validation as user fills form - NEW ORDER
  // Step 1: Credential type must be selected first
  useEffect(() => {
    setCanProceedToReadInOut(credentialType !== "");
  }, [credentialType]);

  // Step 2: Read in/out choice must be made
  useEffect(() => {
    setCanProceedToDoors(requiresReadInOut !== "");
  }, [requiresReadInOut]);

  // Step 3: Doors entered
  useEffect(() => {
    const doors = parseInt(totalDoors);
    if (doors > 0 && !isNaN(doors)) {
      if (requiresReadInOut === "no") {
        setReadInOutDoors("0");
        setCanCalculate(true);
      } else {
        setCanProceedToReadInOutCount(true);
      }
    } else {
      setCanProceedToReadInOutCount(false);
      setCanCalculate(false);
    }
  }, [totalDoors, requiresReadInOut]);

  // Step 4: Read in/out door count (if applicable)
  useEffect(() => {
    if (requiresReadInOut === "yes") {
      const rioCount = parseInt(readInOutDoors);
      const total = parseInt(totalDoors);
      setCanCalculate(rioCount >= 0 && !isNaN(rioCount) && rioCount <= total);
    }
  }, [readInOutDoors, totalDoors, requiresReadInOut]);

  const handleCalculate = async () => {
    setError("");
    setSuccess("");
    
    if (!projectName.trim()) {
      setError("Project name is required");
      return;
    }

    // Validate quote target for ZKTeco staff
    const isAdminUser = userRole === 'admin' || userRole === 'zkteco_admin' || userRole === 'zkteco';
    if (isAdminUser && !quoteTarget) {
      setError("Please select who this quote is for (Distributor, Installer, or End User)");
      return;
    }

    const doors = parseInt(totalDoors);
    const rioCount = parseInt(readInOutDoors || 0);
    const readInOnlyDoors = doors - rioCount;

    if (doors <= 0 || isNaN(doors)) {
      setError("Please enter a valid number of doors");
      return;
    }

    if (rioCount < 0 || rioCount > doors) {
      setError("Read in/out doors cannot exceed total doors");
      return;
    }

    // Validation: System maximum is 84 doors
    if (doors >= 85) {
      setError(`For 85+ doors, please quote Bio CVSecurity.\n\nYour ZKTeco rep can assist you with the build for larger installations.`);
      return;
    }

    // Validation: QR (Weigand-only) reader limit
    if (credentialType === "QR") {
      const totalReaders = rioCount * 2 + readInOnlyDoors * 1;
      const maxWeigandReaders = 21 * 4; // 21 controllers × 4 Weigand max = 84 readers
      
      if (totalReaders > maxWeigandReaders) {
        setError(`Incompatible configuration: QR Code readers require Weigand communication. Your configuration needs ${totalReaders} readers but maximum Weigand capacity is ${maxWeigandReaders} readers (21 controllers × 4 readers max). Please use RS-485 compatible readers (RFID/Keypad/Fingerprint) or reduce door count.`);
        return;
      }
    }

    setLoading(true);
    try {
      // Build controller configuration
      // Strategy: We need to distribute doors across controllers optimally
      // Controllers come in 1, 2, and 4 door sizes
      const controllers = [];
      let controllerId = 1;

      // Function to add a controller
      const addController = (doorCount, readerConfig) => {
        controllers.push({
          id: controllerId++,
          credential_type: credentialType,
          door_count: doorCount,
          reader_config: readerConfig
        });
      };

      // First, handle read in/out doors
      // For QR (Weigand-only) with read in/out, we can only use 2-door controllers
      // because 4-door needs 8 readers but Weigand max is 4
      let remainingRIO = rioCount;
      const isWeigandOnly = credentialType === "QR";
      
      // Strategy: Calculate based on TOTAL DOORS for maximum efficiency
      // Prioritize filling largest controllers possible, accept spare capacity
      
      // COST-OPTIMIZED CONTROLLER ALLOCATION
      // 4-door controllers are most cost-effective (£112/door vs £183/door for 2-door)
      // Always use 4-door controllers and accept spare capacity
      // But track ACTUAL door count per controller for correct reader allocation
      
      const totalDoorsNeeded = rioCount + readInOnlyDoors;
      
      // Helper to add controller with specific actual door count
      const addControllerWithDoors = (controllerSize, actualDoors, readerConfig) => {
        controllers.push({
          id: controllerId++,
          credential_type: credentialType,
          controller_size: controllerSize,  // The controller hardware (always 4 for cost efficiency)
          door_count: actualDoors,          // Actual doors using this controller
          reader_config: readerConfig
        });
      };
      
      if (isWeigandOnly) {
        // For Wiegand-only (QR readers): Limited by Wiegand's 4-reader maximum
        // Read In/Read Out needs 2 readers per door = max 2 doors per controller (Wiegand limit)
        // Read In only needs 1 reader per door = max 4 doors per controller
        // COST OPTIMIZATION: Same rules apply - avoid 1+2 door combo, use 4-door instead
        
        // Handle RIO doors - max 2 per controller due to Wiegand limit
        const fullRioControllers = Math.floor(rioCount / 2);
        const remainingRioDoors = rioCount % 2;
        
        for (let i = 0; i < fullRioControllers; i++) {
          addControllerWithDoors(4, 2, "read_in_out"); // 4-door controller, 2 doors used (Wiegand limit)
        }
        
        if (remainingRioDoors === 1) {
          addControllerWithDoors(1, 1, "read_in_out"); // 1-door controller for 1 RIO door
        }
        
        // Handle read-in only doors - can use full 4 doors per controller
        const fullReadInControllers = Math.floor(readInOnlyDoors / 4);
        const remainingReadInDoors = readInOnlyDoors % 4;
        
        for (let i = 0; i < fullReadInControllers; i++) {
          addControllerWithDoors(4, 4, "read_in");
        }
        
        // Apply same optimization for remaining read-in doors
        if (remainingReadInDoors === 1) {
          addControllerWithDoors(1, 1, "read_in");
        } else if (remainingReadInDoors === 2) {
          addControllerWithDoors(2, 2, "read_in");
        } else if (remainingReadInDoors === 3) {
          addControllerWithDoors(4, 3, "read_in"); // 4-door cheaper than 1+2
        }
      } else {
        // For RS-485 capable readers (RFID/Fingerprint/Keypad):
        // A single panel can MIX read-in/out and read-in-only doors, so we size on
        // the TOTAL door count using the fewest/cheapest panels, then fill each panel.
        // COST OPTIMIZATION RULES (per total doors):
        // - Use 4-door controllers for full sets of 4 (best £/door)
        // - 1 remaining door → 1-door controller
        // - 2 remaining doors → 2-door controller
        // - 3 remaining doors → 4-door controller (NOT 1+2, which costs more)
        // Fill each panel with read-in/out doors first, then read-in-only doors.
        let rioLeft = rioCount;
        let readInLeft = readInOnlyDoors;

        const addMixedController = (hardwareSize, doorsOnPanel) => {
          const rioOnPanel = Math.min(rioLeft, doorsOnPanel);
          const readInOnPanel = doorsOnPanel - rioOnPanel;
          rioLeft -= rioOnPanel;
          readInLeft -= readInOnPanel;
          const readerConfig = rioOnPanel > 0 && readInOnPanel > 0
            ? "mixed"
            : (rioOnPanel > 0 ? "read_in_out" : "read_in");
          controllers.push({
            id: controllerId++,
            credential_type: credentialType,
            controller_size: hardwareSize,
            door_count: doorsOnPanel,
            rio_doors: rioOnPanel,
            read_in_doors: readInOnPanel,
            reader_config: readerConfig,
          });
        };

        // Determine panel sizes for the total door count
        const panelSizes = [];
        let remaining = totalDoorsNeeded;
        while (remaining >= 4) { panelSizes.push(4); remaining -= 4; }
        if (remaining === 1) panelSizes.push(1);
        else if (remaining === 2) panelSizes.push(2);
        else if (remaining === 3) panelSizes.push(4); // single 4-door cheaper than 1-door + 2-door

        panelSizes.forEach((size) => {
          const doorsOnPanel = Math.min(size, rioLeft + readInLeft);
          if (doorsOnPanel > 0) addMixedController(size, doorsOnPanel);
        });
      }

      // Validation: Check controller count (should not exceed 21)
      if (controllers.length > 21) {
        setError(
          `Configuration requires ${controllers.length} controllers, but system maximum is 21 controllers.\n\n` +
          `For 85+ doors, please quote Bio CVSecurity.\n\n` +
          `Your ZKTeco rep can assist you with the build for larger installations.`
        );
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/configure-atlas`, {
        controllers: controllers,
        project_name: projectName,
        quote_reference: quoteReference || null,
        price_modifier: parseFloat(priceModifier) || 0.0
      }, getAuthHeaders());
      
      setResult(response.data);
      setSuccess("Configuration calculated successfully!");
      setTimeout(() => setSuccess(""), 3000);

      // Check if installer price >= £20,000 for project support
      const installerPrice = response.data.final_installer_price || 0;
      if (installerPrice >= 20000 && !supportDeclined) {
        setShowProjectSupport(true);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error calculating configuration";
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = projectName ? `Atlas Quote - ${projectName}` : 'Atlas Access Control Quote';
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!result?.configuration_id) {
      setError('Please calculate first.');
      return;
    }

    try {
      setError("");
      const response = await axios.get(
        `${API}/configurations/${result.configuration_id}/download-pdf`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Atlas_Quote_${projectName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('PDF downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to download PDF.');
    }
  };

  const handleExportCSV = () => {
    if (!result) return;

    let csv = "Category,SKU,Description,Quantity,Unit Price (GBP),Line Total (GBP)\n";
    result.combined_bom.forEach(item => {
      csv += `${item.category},${item.sku},"${item.description}",${item.quantity},${item.unit_price.toFixed(2)},${item.line_total.toFixed(2)}\n`;
    });
    csv += `\nSubtotal,,,,,${result.trade_subtotal.toFixed(2)}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `atlas-access-control-bom-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setCredentialType("");
    setRequiresReadInOut("");
    setTotalDoors("");
    setReadInOutDoors("");
    setResult(null);
    setError("");
    setSuccess("");
  };

  // BOM pricing visibility flags
  const isAdmin = userRole === 'admin' || userRole === 'zkteco_admin' || userRole === 'zkteco';
  const isDistributorUser = userRole === 'distributor' || userRole === 'distributor_admin';
  const showBomDistributorPrice = !installerOnlyCapture && ((isAdmin && quoteTarget === 'distributor') || isDistributorUser);
  const showBomInstallerPrice = isAdmin ? (quoteTarget === 'distributor' || quoteTarget === 'installer') : (isDistributorUser || userRole === 'installer');
  const showBomEndUserPrice = isAdmin && quoteTarget === 'end_user';

  return (
    <div className="bg-slate-50 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        {onBack && (
          <Button
            variant="outline"
            onClick={onBack}
            className="mb-6 print:hidden"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {backLabel}
          </Button>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Configuration Form */}
          <div className="lg:col-span-1 print:hidden space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Atlas Access Control
                </CardTitle>
                <CardDescription>Configure your access control system step-by-step</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Project Information */}
                <div className="space-y-4 pb-4 border-b">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="project-name">Project Name <span className="text-red-500">*</span></Label>
                      <HelpTooltip content={HELP_CONTENT.projectName} />
                    </div>
                    <Input
                      id="project-name"
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="e.g., Office Access Control"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="quote-reference">Quote Reference (Optional)</Label>
                      <HelpTooltip content={HELP_CONTENT.quoteReference} />
                    </div>
                    <Input
                      id="quote-reference"
                      type="text"
                      value={quoteReference}
                      onChange={(e) => setQuoteReference(e.target.value)}
                      placeholder="e.g., Q-ATLAS-001"
                    />
                  </div>
                  
                  {(userRole === 'distributor' || userRole === 'admin' || userRole === 'distributor_admin') && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="price-modifier">Installer Discount (%)</Label>
                        <HelpTooltip content={HELP_CONTENT.priceModifier} />
                      </div>
                      <Input
                        id="price-modifier"
                        type="number"
                        step="0.1"
                        min="0"
                        value={priceModifier}
                        onChange={(e) => setPriceModifier(e.target.value)}
                        placeholder="e.g., 5 for 5% discount"
                      />
                      <p className="text-xs text-slate-500">
                        Enter discount percentage to reduce installer price
                      </p>
                    </div>
                  )}
                  
                  {/* Quote Target Selector - ZKTeco Staff Only */}
                  <QuoteTargetSelector 
                    quoteTarget={quoteTarget} 
                    setQuoteTarget={setQuoteTarget} 
                    userRole={userRole} 
                  />
                </div>

                {/* Step 1: What credential type? */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-sm font-bold">
                      1
                    </div>
                    <Label className="text-base font-semibold">What credential type is required?</Label>
                    <HelpTooltip content={HELP_CONTENT.credentialType} />
                  </div>
                  <RadioGroup 
                    value={credentialType} 
                    onValueChange={setCredentialType}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="QR" id="cred-qr" />
                      <Label htmlFor="cred-qr" className="font-normal cursor-pointer">Dynamic QR</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="RFID" id="cred-rfid" />
                      <Label htmlFor="cred-rfid" className="font-normal cursor-pointer">RFID</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Fingerprint" id="cred-fingerprint" />
                      <Label htmlFor="cred-fingerprint" className="font-normal cursor-pointer">Fingerprint (Biometric)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Keypad" id="cred-keypad" />
                      <Label htmlFor="cred-keypad" className="font-normal cursor-pointer">Keypad</Label>
                    </div>
                  </RadioGroup>
                  {credentialType && (
                    <p className="text-sm text-green-600">✓ Credential type: {credentialType}</p>
                  )}
                </div>

                {/* Step 2: Do you require read in/read out? */}
                {canProceedToReadInOut && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-sm font-bold">
                        2
                      </div>
                      <Label className="text-base font-semibold">Do you require read in/read out?</Label>
                      <HelpTooltip content={HELP_CONTENT.readInOut} />
                    </div>
                    <RadioGroup 
                      value={requiresReadInOut} 
                      onValueChange={(value) => {
                        setRequiresReadInOut(value);
                        if (value === "no") {
                          setReadInOutDoors("0");
                        } else {
                          setReadInOutDoors("");
                        }
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="rio-yes" />
                        <Label htmlFor="rio-yes" className="font-normal cursor-pointer">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="rio-no" />
                        <Label htmlFor="rio-no" className="font-normal cursor-pointer">No (Read In Only)</Label>
                      </div>
                    </RadioGroup>
                    {requiresReadInOut && (
                      <p className="text-sm text-green-600">
                        ✓ {requiresReadInOut === "yes" ? "Read In/Read Out required" : "Read In Only"}
                      </p>
                    )}
                  </div>
                )}

                {/* Step 3: How many doors? */}
                {canProceedToDoors && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-sm font-bold">
                        3
                      </div>
                      <Label className="text-base font-semibold">How many doors?</Label>
                      <HelpTooltip content={HELP_CONTENT.doorCount} />
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={totalDoors}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTotalDoors(value);
                        // Check if 85 or above
                        if (value && parseInt(value) >= 85) {
                          setShowBioCVWarning(true);
                        } else {
                          setShowBioCVWarning(false);
                        }
                      }}
                      placeholder="Enter total number of doors"
                      className="text-lg"
                    />
                    {showBioCVWarning && (
                      <Alert className="bg-amber-50 border-amber-300">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-900">
                          <strong>For 85+ doors, please quote Bio CVSecurity.</strong>
                          <br />
                          Your ZKTeco rep can assist you with the build for larger installations.
                        </AlertDescription>
                      </Alert>
                    )}
                    {totalDoors && parseInt(totalDoors) > 0 && !showBioCVWarning && (
                      <p className="text-sm text-green-600">✓ Total doors: {totalDoors}</p>
                    )}
                  </div>
                )}

                {/* Step 4: How many doors are read in/read out? */}
                {canProceedToReadInOutCount && requiresReadInOut === "yes" && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-sm font-bold">
                        4
                      </div>
                      <Label className="text-base font-semibold">How many doors are read in/read out?</Label>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      max={totalDoors}
                      value={readInOutDoors}
                      onChange={(e) => setReadInOutDoors(e.target.value)}
                      placeholder={`Max: ${totalDoors} doors`}
                      className="text-lg"
                    />
                    {readInOutDoors && parseInt(readInOutDoors) >= 0 && (
                      <div className="text-sm space-y-1">
                        <p className="text-green-600">✓ Read In/Out: {readInOutDoors} doors</p>
                        <p className="text-blue-600">• Read In Only: {parseInt(totalDoors) - parseInt(readInOutDoors)} doors</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Reader Count Summary */}
                {totalDoors && parseInt(totalDoors) > 0 && canCalculate && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-sm font-semibold text-blue-900 mb-2">📊 Readers Required:</div>
                    <div className="text-3xl font-bold text-blue-600">
                      {parseInt(readInOutDoors || 0) * 2 + (parseInt(totalDoors) - parseInt(readInOutDoors || 0)) * 1} readers
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      {parseInt(readInOutDoors || 0) > 0 && (
                        <div>• Read In/Out: {parseInt(readInOutDoors || 0)} doors × 2 = {parseInt(readInOutDoors || 0) * 2} readers</div>
                      )}
                      {(parseInt(totalDoors) - parseInt(readInOutDoors || 0)) > 0 && (
                        <div>• Read In Only: {parseInt(totalDoors) - parseInt(readInOutDoors || 0)} doors × 1 = {parseInt(totalDoors) - parseInt(readInOutDoors || 0)} readers</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Error/Success Messages */}
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="bg-green-50 border-green-200">
                    <AlertDescription className="text-green-800">{success}</AlertDescription>
                  </Alert>
                )}

                {/* Calculate Button */}
                <div className="pt-4 space-y-2">
                  <Button 
                    onClick={handleCalculate} 
                    className="w-full"
                    disabled={loading || !canCalculate}
                  >
                    {loading ? "Calculating..." : "Calculate & Generate Quote"}
                  </Button>
                  {result && (
                    <Button 
                      onClick={handleReset} 
                      variant="outline"
                      className="w-full"
                    >
                      Start New Configuration
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2">
            {result && (
              <div className="space-y-6">
                {/* Action Buttons */}
                <div className="flex gap-2 print:hidden">
                  <Button 
                    onClick={handleExportCSV} 
                    variant="outline"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button 
                    onClick={handleAddSundryItem} 
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Sundry Line
                  </Button>
                </div>

                {/* Weigand Warnings */}
                {result.controllers.some(c => c.has_weigand_warning) && (
                  <Alert className="bg-amber-50 border-amber-300">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-900">
                      <strong>⚠️ Warning:</strong> Because you&apos;ve selected Read In/Read Out with Wiegand readers, 4-door controllers can only operate as 2-door controllers.
                      <div className="mt-2">
                        <strong>Impact:</strong> {result.controllers.filter(c => c.has_weigand_warning).length} controller(s) affected • Total Effective Doors: {result.total_effective_doors} doors (reduced from {totalDoors} doors)
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* System Summary */}
                <Card className="print:hidden">
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="text-lg">System Configuration Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-white p-4 rounded border">
                        <div className="text-xs text-slate-600 mb-1">Total Doors</div>
                        <div className="text-2xl font-bold text-slate-900">{totalDoors}</div>
                      </div>
                      <div className="bg-white p-4 rounded border">
                        <div className="text-xs text-slate-600 mb-1">Effective Doors</div>
                        <div className="text-2xl font-bold text-slate-900">{result.total_effective_doors}</div>
                      </div>
                      <div className="bg-white p-4 rounded border">
                        <div className="text-xs text-slate-600 mb-1">Controllers</div>
                        <div className="text-2xl font-bold text-slate-900">{result.total_controllers}</div>
                      </div>
                      <div className="bg-white p-4 rounded border">
                        <div className="text-xs text-slate-600 mb-1">Total Readers</div>
                        <div className="text-2xl font-bold text-slate-900">{result.total_readers}</div>
                      </div>
                      <div className="bg-white p-4 rounded border">
                        <div className="text-xs text-slate-600 mb-1">Credential Type</div>
                        <div className="text-lg font-bold text-slate-900">{credentialType}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Combined BOM */}
                <div ref={quoteBoxRef}>
                <Card>
                  <CardHeader className="bg-slate-900 text-white flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Bill of Materials
                      </CardTitle>
                      <CardDescription className="text-slate-300">
                        Complete system components - License Tier: {result.license_tier}
                      </CardDescription>
                    </div>
                    {isDistributorUser ? (
                      <div className="flex gap-2" data-html2canvas-ignore="true">
                        <Button 
                          variant="outline" 
                          size="sm"
                          data-testid="download-distributor-quote-btn"
                          onClick={() => handleDownloadQuoteImage(false)}
                          className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Distributor Quote
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          data-testid="download-installer-quote-btn"
                          onClick={() => handleDownloadQuoteImage(true)}
                          className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Installer Quote
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        data-html2canvas-ignore="true"
                        onClick={() => handleDownloadQuoteImage(false)}
                        className="bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Download Quote
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">Category</th>
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">SKU</th>
                            <th className="text-left py-3 px-2 font-semibold text-slate-700">Description</th>
                            <th className="text-right py-3 px-2 font-semibold text-slate-700">Qty</th>
                            {!installerOnlyCapture && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-slate-700">
                                  {showBomDistributorPrice ? 'Cost Price' : 'Unit Price'}
                                </th>
                                <th className="text-right py-3 px-2 font-semibold text-slate-700">
                                  {showBomDistributorPrice ? 'Cost Total' : 'Line Total'}
                                </th>
                              </>
                            )}
                            {showBomInstallerPrice && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-green-700">{userRole === 'installer' ? 'Unit Price' : 'Sell Price'}</th>
                                <th className="text-right py-3 px-2 font-semibold text-green-700">{userRole === 'installer' ? 'Line Total' : 'Sell Total'}</th>
                              </>
                            )}
                            {showBomEndUserPrice && (
                              <>
                                <th className="text-right py-3 px-2 font-semibold text-orange-700">End User Price</th>
                                <th className="text-right py-3 px-2 font-semibold text-orange-700">EU Total</th>
                              </>
                            )}
                            <th className="text-center py-3 px-2 font-semibold text-slate-700 print:hidden"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.combined_bom.map((item, index) => (
                            <tr 
                              key={index} 
                              className="border-b border-slate-100 hover:bg-slate-50"
                            >
                              <td className="py-3 px-2 text-slate-600">{item.category}</td>
                              <td className="py-3 px-2">
                                {item.product_url ? (
                                  <a
                                    href={
                                      item.product_url.startsWith('http') 
                                        ? item.product_url 
                                        : `${BACKEND_URL}${item.product_url}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline font-medium cursor-pointer"
                                    title="View datasheet"
                                  >
                                    {item.sku}
                                  </a>
                                ) : (
                                  <span className="text-slate-900 font-medium">{item.sku}</span>
                                )}
                              </td>
                              <td className="py-3 px-2 text-slate-600">{item.description}</td>
                              <td className="py-3 px-2 text-right text-slate-900">{item.quantity}</td>
                              {!installerOnlyCapture && (
                                <>
                                  <td className="py-3 px-2 text-right text-slate-900">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-slate-900 font-medium">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              {showBomInstallerPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-green-700 font-medium">£{item.installer_price?.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-green-700 font-semibold">£{item.installer_total?.toFixed(2)}</td>
                                </>
                              )}
                              {showBomEndUserPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-orange-700 font-medium">£{(item.end_user_price || 0).toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-orange-700 font-semibold">£{(item.end_user_total || 0).toFixed(2)}</td>
                                </>
                              )}
                              <td className="print:hidden"></td>
                            </tr>
                          ))}
                          {/* Sundry Items in B.O.M */}
                          {sundryItems.map((item, index) => (
                            <tr 
                              key={`sundry-${index}`} 
                              className="border-b border-amber-200 bg-amber-50 hover:bg-amber-100"
                            >
                              <td className="py-3 px-2 text-amber-700 font-medium">Sundry</td>
                              <td className="py-3 px-2 text-amber-800 font-mono text-xs">{item.sku || '-'}</td>
                              <td className="py-3 px-2 text-amber-800">{item.description}</td>
                              <td className="py-3 px-2 text-right text-amber-900">{item.quantity}</td>
                              {!installerOnlyCapture && (
                                <>
                                  <td className="py-3 px-2 text-right text-amber-900">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-amber-900 font-medium">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              {showBomInstallerPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-amber-700">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-amber-700 font-semibold">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              {showBomEndUserPrice && (
                                <>
                                  <td className="py-3 px-2 text-right text-amber-700">£{item.unit_price.toFixed(2)}</td>
                                  <td className="py-3 px-2 text-right text-amber-700 font-semibold">£{item.line_total.toFixed(2)}</td>
                                </>
                              )}
                              <td className="py-3 px-2 text-center print:hidden">
                                <div className="flex justify-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-amber-700 hover:text-amber-900"
                                    onClick={() => handleEditSundryItem(index)}
                                  >
                                    <FileText className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteSundryItem(index)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          {(showBomDistributorPrice || showBomInstallerPrice || showBomEndUserPrice) ? (
                            <>
                              <tr className="border-t-2 border-slate-300 bg-slate-50">
                                <td colSpan="4" className="py-3 px-2 text-right font-semibold">Subtotals:</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-purple-700">Distributor:</td>
                                    <td className="py-3 px-2 text-right font-bold text-purple-700 text-lg">£{result.trade_subtotal.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-green-700">{userRole === 'installer' ? 'Subtotal:' : 'Installer:'}</td>
                                    <td className="py-3 px-2 text-right font-bold text-green-700 text-lg">
                                      £{result.installer_subtotal?.toFixed(2)}
                                    </td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-3 px-2 text-right font-semibold text-orange-700">End User:</td>
                                    <td className="py-3 px-2 text-right font-bold text-orange-700 text-lg">
                                      £{(result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0).toFixed(2)}
                                    </td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              <tr className="bg-slate-50">
                                <td colSpan="4" className="py-3 px-2 text-right font-medium text-slate-700">Delivery:</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-purple-700">£{(result.delivery_cost || 0).toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-green-700">£{(result.delivery_cost_installer ?? result.delivery_cost ?? 0).toFixed(2)}</td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-3 px-2"></td>
                                    <td className="py-3 px-2 text-right font-semibold text-orange-700">£{(result.delivery_cost_installer ?? result.delivery_cost ?? 0).toFixed(2)}</td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              {result.price_modifier !== 0 && showBomInstallerPrice && (
                                <tr className="bg-blue-50">
                                  <td colSpan={4 + (showBomDistributorPrice ? 2 : 0)} className="py-3 px-2 text-right font-medium text-blue-800">
                                    Installer Discount ({result.price_modifier}%):
                                  </td>
                                  <td className="py-3 px-2"></td>
                                  <td className="py-3 px-2 text-right font-semibold text-blue-900">
                                    -£{result.modifier_amount.toFixed(2)}
                                  </td>
                                  <td className="print:hidden"></td>
                                </tr>
                              )}
                              <tr className="border-t-2 border-slate-400 bg-slate-900 text-white">
                                <td colSpan="4" className="py-4 px-2 text-right font-bold text-lg">Final Totals:</td>
                                {showBomDistributorPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-purple-300">Distributor:</td>
                                    <td className="py-4 px-2 text-right font-bold text-xl text-purple-300">£{(result.subtotal_with_delivery ?? result.trade_subtotal).toFixed(2)}</td>
                                  </>
                                )}
                                {showBomInstallerPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-green-300">{userRole === 'installer' ? 'Your Total:' : 'Installer:'}</td>
                                    <td className="py-4 px-2 text-right font-bold text-2xl text-green-400">£{result.final_installer_price?.toFixed(2)}</td>
                                  </>
                                )}
                                {showBomEndUserPrice && (
                                  <>
                                    <td className="py-4 px-2 text-right font-bold text-orange-300">End User:</td>
                                    <td className="py-4 px-2 text-right font-bold text-2xl text-orange-400">
                                      £{(result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0).toFixed(2)}
                                    </td>
                                  </>
                                )}
                                <td className="print:hidden"></td>
                              </tr>
                              {sundryItems.length > 0 && (
                                <>
                                  <tr className="bg-amber-50">
                                    <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0) + (showBomEndUserPrice ? 1 : 0)} className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
                                    <td className="py-3 px-2 text-right font-bold text-amber-800">£{calculateSundryTotal(sundryItems).toFixed(2)}</td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                  <tr className="bg-green-100 border-t-2 border-green-400">
                                    <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0) + (showBomEndUserPrice ? 1 : 0)} className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
                                    <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">
                                      £{showBomEndUserPrice
                                        ? ((result.combined_bom || []).reduce((sum, item) => sum + (item.end_user_total || 0), 0) + calculateSundryTotal(sundryItems)).toFixed(2)
                                        : (result.final_installer_price + calculateSundryTotal(sundryItems)).toFixed(2)}
                                    </td>
                                    <td className="print:hidden"></td>
                                  </tr>
                                </>
                              )}
                              {showBomDistributorPrice && (
                                <tr className="bg-green-50">
                                  <td colSpan={4 + (showBomDistributorPrice ? 2 : 0) + (showBomInstallerPrice ? 1 : 0)} className="py-3 px-2 text-right font-semibold text-green-800">Your Margin:</td>
                                  <td className="py-3 px-2 text-right font-bold text-green-800 text-lg">
                                    £{result.distributor_margin?.toFixed(2)}
                                    <span className="text-sm ml-2">
                                      ({((result.distributor_margin / result.final_installer_price) * 100).toFixed(1)}%)
                                    </span>
                                  </td>
                                  <td className="print:hidden"></td>
                                </tr>
                              )}
                            </>
                          ) : (
                            <>
                              {/* Installer/End User view - shows only their cost price */}
                              <tr className="border-t-2 border-slate-300 bg-slate-50">
                                <td colSpan="5" className="py-3 px-2 text-right font-semibold text-slate-900">Equipment Subtotal:</td>
                                <td className="py-3 px-2 text-right font-bold text-slate-900 text-lg">£{result.trade_subtotal.toFixed(2)}</td>
                              </tr>
                              <tr className="bg-slate-50">
                                <td colSpan="5" className="py-3 px-2 text-right font-medium text-slate-700">Delivery:</td>
                                <td className="py-3 px-2 text-right font-semibold text-slate-900">£{result.delivery_cost.toFixed(2)}</td>
                              </tr>
                              <tr className="border-t-2 border-slate-400 bg-slate-900 text-white">
                                <td colSpan="5" className="py-4 px-2 text-right font-bold text-lg">YOUR TOTAL COST:</td>
                                <td className="py-4 px-2 text-right font-bold text-2xl">£{result.subtotal_with_delivery.toFixed(2)}</td>
                              </tr>
                              {sundryItems.length > 0 && (
                                <>
                                  <tr className="bg-amber-50">
                                    <td colSpan="5" className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
                                    <td className="py-3 px-2 text-right font-bold text-amber-800">£{calculateSundryTotal(sundryItems).toFixed(2)}</td>
                                  </tr>
                                  <tr className="bg-green-100 border-t-2 border-green-400">
                                    <td colSpan="5" className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
                                    <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">£{(result.subtotal_with_delivery + calculateSundryTotal(sundryItems)).toFixed(2)}</td>
                                  </tr>
                                </>
                              )}
                            </>
                          )}
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
                </div>
              </div>
            )}

            {!result && (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-slate-500">
                    <Calculator className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium mb-2">Answer the questions to configure your system</p>
                    <p className="text-sm">Follow the step-by-step guide on the left to create your quote</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Project Support Dialog */}
      <ProjectSupportDialog
        open={showProjectSupport}
        onOpenChange={(open) => {
          setShowProjectSupport(open);
          // If user closes dialog without submitting, mark as declined
          if (!open) {
            setSupportDeclined(true);
          }
        }}
        quoteData={{
          project_name: projectName,
          quote_reference: quoteReference,
          credential_type: credentialType,
          total_doors: totalDoors,
          combined_bom: result?.combined_bom,
          installer_price: result?.final_installer_price,
          installer_total: result?.final_installer_price,
          total: result?.final_total
        }}
        configuratorType="Atlas Access Control"
      />

      {/* Sundry Item Dialog */}
      <Dialog open={showSundryDialog} onOpenChange={setShowSundryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSundryIndex !== null ? 'Edit Sundry Item' : 'Add Sundry Item'}</DialogTitle>
            <DialogDescription>
              Add custom line items such as labour, cabling, commissioning, travel, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sundry-sku">SKU (optional)</Label>
              <Input
                id="sundry-sku"
                placeholder="e.g. LABOUR-001"
                value={sundryForm.sku}
                onChange={(e) => setSundryForm({...sundryForm, sku: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sundry-description">Description *</Label>
              <Input
                id="sundry-description"
                placeholder="e.g. Installation labour, Cabling, Travel"
                value={sundryForm.description}
                onChange={(e) => setSundryForm({...sundryForm, description: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sundry-quantity">Quantity *</Label>
                <Input
                  id="sundry-quantity"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="1"
                  value={sundryForm.quantity}
                  onChange={(e) => setSundryForm({...sundryForm, quantity: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sundry-price">Unit Price (£) *</Label>
                <Input
                  id="sundry-price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={sundryForm.unit_price}
                  onChange={(e) => setSundryForm({...sundryForm, unit_price: e.target.value})}
                />
              </div>
            </div>
            {sundryForm.quantity && sundryForm.unit_price && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Line Total:</span>
                  <span className="font-semibold">
                    £{((parseFloat(sundryForm.quantity) || 0) * (parseFloat(sundryForm.unit_price) || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSundryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSundryItem}>
              {editingSundryIndex !== null ? 'Update' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AtlasAccessControl;
